import {
  useEmitCoreHotelsRawEvent,
  useHotelSlot,
} from "@b2bportal/core-hotels";
import {
  useHotelCardTracking,
  HotelsRawEventName,
} from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ImageCarousel } from "@tenant/hotels";
import { Tags } from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import { TripAdvisorRating } from "../TripAdvisorRating";
import defaultStyles from "./HotelMapCard.module.scss";
import { Rewards } from "@/components/Rewards";
import { getLodgingAddress } from "@/utils/lodging";
import { calculatePointsEarnedForLodging } from "@/utils/points";

export interface HotelMapCardComponentProps {
  hotel: Lodging;
  dateRange: DateRange;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelMapCard;

export const HotelMapCard = ({
  hotel,
  dateRange,
}: HotelMapCardComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { ref } = useHotelCardTracking({ hotel });
  const { t } = useI18nContext();
  const emitHotelSwipedCarousel = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelSwipedCarousel
  );

  const onNavigateToSlide = () => {
    emitHotelSwipedCarousel({ properties: { hotelId: hotel.lodging.id } });
  };

  const HotelSlot = useHotelSlot();
  return (
    <HotelSlot.HotelLink hotel={hotel}>
      <div ref={ref} className={block}>
        <div className={cn("carousel-container")}>
          <ImageCarousel
            imageUrlsArray={getImageUrlsArray(hotel.lodging.media)}
            onNavigateToNextSlide={onNavigateToSlide}
            onNavigateToPrevSlide={onNavigateToSlide}
          />
        </div>
        <div className={cn("rewards-container")}>
          <Rewards amount={calculatePointsEarnedForLodging(hotel)} />
        </div>
        <div className={cn("rating-container")}>
          <TripAdvisorRating
            tripAdvisorReviews={hotel.lodging.tripAdvisorReviews}
            className={cn("tripadvisor-rating")}
            isMinimal={true}
          />
        </div>
        <div className={cn("title-container")}>
          <h2 className={cn("title")}>{hotel.lodging.name}</h2>
        </div>
        <div className={cn("location-container")}>
          <Icon
            iconName={IconNameEnum.mapPin}
            className={cn("location-icon")}
          />{" "}
          <span>{getLodgingAddress(hotel.lodging)}</span>
        </div>
        <div className={cn("tags-container")}>
          <Tags hotel={hotel} className={cn("tags")} />
        </div>
        <div className={cn("features-container")}>
          {hotel.isFreeCancel && (
            <div className={cn("free-cancellation")}>
              {t(
                "core-hotels.availabilityPage.results.hotelCard.features.freeCancellation"
              )}
            </div>
          )}
        </div>
        <div className={cn("price-container")}>
          <HotelSlot.HotelCardPrice hotel={hotel} dateRange={dateRange} />
        </div>
      </div>
    </HotelSlot.HotelLink>
  );
};
